import { BreadcrumbJsonLd, NextSeo } from 'next-seo'
import Head from 'next/head'
import { useRouter } from 'next/router'

import { PressFragment } from '@/generated/contentful'

import { getBaseUrl } from '@/lib/contentful/utils'
import { PressAvailableSlugs } from '@/lib/seo/types'

import { isNonNullable } from '@/utils/isNonNullable'
import { getSeoLocale } from '@/utils/locales/getSeoLocale'
import { getLocalizedFromEnglish } from '@/utils/locales/localized-routes/getLocalizedFromEnglish'
import getLocalizedPathFromEnglish from '@/utils/locales/localized-routes/getLocalizedPathFromEnglish'
import { NewsArticleMarkupContent } from '@/utils/seo/newsArticleMarkupContent'
import { useUrl } from '@/utils/url/useUrl'

import { useEntry } from '../Press.hooks'

type Props = {
	children: React.ReactNode
	headline: string
	articleBody: string
	publishedDate: string
	entryItem: PressFragment
}

export function PressPostMeta({
	children,
	headline,
	articleBody,
	publishedDate,
	entryItem
}: Props) {
	const entry = useEntry()
	const { locale = 'en-US' } = useRouter()
	const url = useUrl({ locale })
	const baseUrl = `${url?.protocol}//${url?.host}`

	if ('error' in entry) return <>{children}</>

	const {
		seoMetadata,
		enabled_en_US,
		enabled_en_AU,
		enabled_en_GB,
		enabled_en_SG,
		enabled_es_MX,
		enabled_es_ES,
		enabled_it_IT,
		enabled_fr_FR,
		enabled_nl_NL,
		enabled_de_DE,
		enabled_de_AT,
		enabled_da_DK,
		enabled_sv_SE,
		enabled_nb_NO
	} = entry.data

	let title = seoMetadata?.title ?? entry.data.title ?? 'Press Releases | BigCommerce'

	title = seoMetadata?.includeTitleTemplate ? `${title}| BigCommerce` : title

	const description =
		seoMetadata?.description ??
		'Learn how BigCommerce can fuel your business with all the capabilities of enterprise ecommerce—without the cost or complexity.'

	type LocaleMap = Record<string, PressAvailableSlugs>

	const getLocalizedSlug = (locale: string): PressAvailableSlugs => {
		const localesMap: LocaleMap = {
			'en-US': 'slug_en_US',
			'en-AU': 'slug_en_AU',
			'en-GB': 'slug_en_GB',
			'en-SG': 'slug_en_SG',
			'es-MX': 'slug_es_MX',
			'es-ES': 'slug_es_ES',
			'it-IT': 'slug_it_IT',
			'fr-FR': 'slug_fr_FR',
			'nl-NL': 'slug_nl_NL',
			'de-DE': 'slug_de_DE',
			'de-AT': 'slug_de_AT',
			'da-DK': 'slug_da_DK',
			'sv-SE': 'slug_sv_SE',
			'nb-NO': 'slug_nb_NO'
		}

		return localesMap[locale]
	}

	let localeSlug: PressAvailableSlugs = getLocalizedSlug(locale)

	return (
		<>
			<Head>
				{enabled_en_US && <meta name="og:locale:alternate" content="en_US" key="en_US" />}
				{enabled_en_AU && <meta name="og:locale:alternate" content="en_AU" key="en_AU" />}
				{enabled_en_GB && <meta name="og:locale:alternate" content="en_GB" key="en_GB" />}
				{enabled_en_SG && <meta name="og:locale:alternate" content="en_SG" key="en_SG" />}
				{enabled_fr_FR && <meta name="og:locale:alternate" content="fr_FR" key="fr_FR" />}
				{enabled_it_IT && <meta name="og:locale:alternate" content="it_IT" key="it_IT" />}
				{enabled_nl_NL && <meta name="og:locale:alternate" content="nl_NL" key="nl_NL" />}
				{enabled_es_ES && <meta name="og:locale:alternate" content="es_ES" key="es_ES" />}
				{enabled_es_MX && <meta name="og:locale:alternate" content="es_MX" key="es_MX" />}
				{enabled_de_DE && <meta name="og:locale:alternate" content="de_DE" key="de_DE" />}
				{enabled_de_AT && <meta name="og:locale:alternate" content="de_AT" key="de_AT" />}
				{enabled_da_DK && <meta name="og:locale:alternate" content="da_DK" key="da_DK" />}
				{enabled_sv_SE && <meta name="og:locale:alternate" content="sv_SE" key="sv_SE" />}
				{enabled_nb_NO && <meta name="og:locale:alternate" content="nb_NO" key="nb_NO" />}
			</Head>
			<NextSeo
				title={title}
				description={description}
				canonical={
					entry.data.seoMetadata?.canonicalUrl ||
					`${baseUrl}${getLocalizedFromEnglish(
						locale as Locale,
						`/press/releases/${entry.data.slug}/`
					)}`
				}
				languageAlternates={[
					enabled_nb_NO
						? {
								hrefLang: 'nb-NO',
								href: `${getBaseUrl('nb-NO')}${getLocalizedPathFromEnglish(
									'nb-NO',
									'/press/releases/'
								)}${entry.data.slug_nb_NO}/`
						  }
						: null,
					enabled_sv_SE
						? {
								hrefLang: 'sv-SE',
								href: `${getBaseUrl('sv-SE')}${getLocalizedPathFromEnglish(
									'sv-SE',
									'/press/releases/'
								)}${entry.data.slug_sv_SE}/`
						  }
						: null,
					enabled_da_DK
						? {
								hrefLang: 'da-DK',
								href: `${getBaseUrl('da-DK')}${getLocalizedPathFromEnglish(
									'da-DK',
									'/press/releases/'
								)}${entry.data.slug_da_DK}/`
						  }
						: null,
					enabled_de_AT
						? {
								hrefLang: 'de-AT',
								href: `${getBaseUrl('de-AT')}${getLocalizedPathFromEnglish(
									'de-AT',
									'/press/releases/'
								)}${entry.data.slug_de_AT}/`
						  }
						: null,
					enabled_en_SG
						? {
								hrefLang: 'en-SG',
								href: `${getBaseUrl('en-SG')}${getLocalizedPathFromEnglish(
									'en-SG',
									'/press/releases/'
								)}${entry.data.slug_en_SG}/`
						  }
						: null,
					enabled_es_MX
						? {
								hrefLang: 'es-MX',
								href: `${getBaseUrl('es-MX')}${getLocalizedPathFromEnglish(
									'es-MX',
									'/press/releases/'
								)}${entry.data.slug_es_MX}/`
						  }
						: null,
					enabled_de_DE
						? {
								hrefLang: 'de-DE',
								href: `${getBaseUrl('de-DE')}${getLocalizedPathFromEnglish(
									'de-DE',
									'/press/releases/'
								)}${entry.data.slug_de_DE}/`
						  }
						: null,
					enabled_es_ES
						? {
								hrefLang: 'es-ES',
								href: `${getBaseUrl('es-ES')}${getLocalizedPathFromEnglish(
									'es-ES',
									'/press/releases/'
								)}${entry.data.slug_es_ES}/`
						  }
						: null,
					enabled_fr_FR
						? {
								hrefLang: 'fr-FR',
								href: `${getBaseUrl('fr-FR')}${getLocalizedPathFromEnglish(
									'fr-FR',
									'/press/releases/'
								)}${entry.data.slug_fr_FR}/`
						  }
						: null,
					enabled_nl_NL
						? {
								hrefLang: 'nl-NL',
								href: `${getBaseUrl('nl-NL')}${getLocalizedPathFromEnglish(
									'nl-NL',
									'/press/releases/'
								)}${entry.data.slug_nl_NL}/`
						  }
						: null,
					enabled_it_IT
						? {
								hrefLang: 'it-IT',
								href: `${getBaseUrl('it-IT')}${getLocalizedPathFromEnglish(
									'it-IT',
									'/press/releases/'
								)}${entry.data.slug_it_IT}/`
						  }
						: null,
					enabled_en_AU
						? {
								hrefLang: 'en-AU',
								href: `${getBaseUrl('en-AU')}${getLocalizedPathFromEnglish(
									'en-AU',
									'/press/releases/'
								)}${entry.data.slug_en_AU}/`
						  }
						: null,
					enabled_en_GB
						? {
								hrefLang: 'en-GB',
								href: `${getBaseUrl('en-GB')}${getLocalizedPathFromEnglish(
									'en-GB',
									'/press/releases/'
								)}${entry.data.slug_en_GB}/`
						  }
						: null,
					enabled_en_US
						? {
								hrefLang: 'en-US',
								href: `${getBaseUrl('en-US')}/press/releases/${entry.data.slug_en_US}/`
						  }
						: null,
					enabled_en_US
						? {
								hrefLang: 'x-default',
								href: `${getBaseUrl('en-US')}/press/releases/${entry.data.slug_en_US}/`
						  }
						: null
				].filter(isNonNullable)}
				openGraph={{
					title,
					description,
					locale: getSeoLocale(locale),
					url: `${baseUrl}${getLocalizedFromEnglish(
						locale as Locale,
						`/press/releases/${entry.data.slug}`
					)}`,
					siteName: 'BigCommerce',
					images: seoMetadata?.image?.url
						? [
								{
									url: seoMetadata?.image?.url,
									width: seoMetadata?.image?.width,
									height: seoMetadata?.image?.height,
									alt: 'BigCommerce',
									type: 'image/jpeg'
								}
						  ]
						: [
								{
									url: 'https://www-cdn.bigcommerce.com/assets/_1200x630_crop_center-center_82_none/BigCommerce-Social-Image-Generic-Facebook.jpg?mtime=1681929501',
									width: 1200,
									height: 630,
									alt: 'BigCommerce',
									type: 'image/jpeg'
								}
						  ]
				}}
				nofollow={Boolean(seoMetadata?.nofollow)}
				noindex={Boolean(seoMetadata?.noindex)}
			/>

			<BreadcrumbJsonLd
				itemListElements={[
					{
						position: 1,
						name: 'Homepage',
						item: `${getBaseUrl(locale)}/`
					},
					{
						position: 2,
						name: 'Press Center',
						item: `${getBaseUrl(locale)}${getLocalizedFromEnglish(locale as Locale, `/press/`)}`
					},
					{
						position: 3,
						name: 'Press Releases',
						item: `${getBaseUrl(locale)}${getLocalizedFromEnglish(
							locale as Locale,
							`/press/releases/`
						)}`
					},
					{
						position: 4,
						name: entry.data.title,
						item: `${getBaseUrl(locale)}${getLocalizedFromEnglish(
							locale as Locale,
							`/press/releases/`
						)}${entry.data.slug}/`
					}
				]}
			/>
			<script
				type="application/ld+json"
				dangerouslySetInnerHTML={{
					__html: NewsArticleMarkupContent(
						`${getBaseUrl(locale)}${getLocalizedFromEnglish(locale as Locale, `/press/releases/`)}${
							url?.pathname === '/' ? entryItem[localeSlug] : url?.pathname
						}/`,
						headline,
						articleBody,
						publishedDate
					)
				}}
			/>
			{children}
		</>
	)
}
